import React, { useEffect, useMemo, useRef, useState } from "react"
import {
  faFileInvoiceDollar,
  faMoneyBillTransfer,
  faPercent,
  faReceipt,
  faSackDollar,
  faUsers,
} from "@fortawesome/free-solid-svg-icons"
import { useTranslation } from "react-i18next"
import { useLocation } from "react-router-dom"
import GetSpace from "../../utils/space"
import { convertToLocalMoney, fromInputDate } from "../../Objects/Generics/Helps"
import { useGetOrder } from "../../hooks/Order/useGetOrder"
import { companyMemory, employeeMemory } from "../../Objects/Memory/Memory"
import { DailyCalculate } from "../../Objects/Calculates/DailyCalculate"
import { EEnableStatusType } from "../../Enum/EEnableStatusType"
import { getRestaurantNow } from "../../utils/timeZone"
import { CashFlowPeriodFilterPopup } from "./CashFlowPeriodFilterPopup"
import { CashFlowMetricsLoading } from "./CashFlowMetricsLoading"
import {
  buildCompactLabelValueListHtml,
  buildCompactSectionHtml,
  buildSummaryCardsHtml,
  buildTableSectionHtml,
  escapeHtml,
  openPrintReport,
} from "../../utils/printReport"
import {
  buildCashFlowMetricsCacheKey,
  deleteCashFlowMetricsCache,
  getCashFlowMetricsCache,
  setCashFlowMetricsCache,
} from "./CashFlowMetricsCache"
import { loadCashFlowOrders } from "./loadCashFlowOrders"
import { FinancialPageLayout } from "./components/FinancialPageLayout"
import { FinancialPageHeader } from "./components/FinancialPageHeader"
import { FinancialKpiCard as MetricCard } from "./components/FinancialKpiCard"
import { FinancialChartCard } from "./components/FinancialChartCard"
import { FinancialDonutChart } from "./components/charts/FinancialDonutChart"
import { FinancialHorizontalBarChart } from "./components/charts/FinancialHorizontalBarChart"
import { FinancialDataTable } from "./components/FinancialDataTable"
import type { FinancialTableColumn } from "./types/financialUi"
import { limitFinancialRanking } from "./utils/financialChartUtils"
import { CashFlowPrintFormatPopup, type CashFlowPrintFormat } from "./CashFlowPrintFormatPopup"

interface PayrollRow {
  id: string
  name: string
  role: string
  isMaster: boolean
  commissionPercent: number
  fixedSalary: number
  netRevenue: number
  closedOrders: number
  commissionAmount: number
  payrollTotal: number
}

export const PayrollSummaryPage: React.FC = () => {
  const { t } = useTranslation("pages")

  const getHelpBullets = (key: string) => {
    const translated = t(key, { returnObjects: true })
    return Array.isArray(translated) ? translated.map((item) => String(item)) : []
  }

  const location = useLocation()

  const [today] = useState(() => getRestaurantNow(companyMemory.timeZoneId))

  const [dateStart, setDateStart] = useState(new Date(today.getFullYear(), today.getMonth(), 1).toInputDate())

  const [dateFinal, setDateFinal] = useState(today.toInputDate())

  const [showPeriodFilter, setShowPeriodFilter] = useState(false)

  const [showPrintFormatPopup, setShowPrintFormatPopup] = useState(false)

  const [calculate, setCalculate] = useState<DailyCalculate>(new DailyCalculate())

  const [isMetricsLoading, setIsMetricsLoading] = useState(false)

  const [refreshNonce, setRefreshNonce] = useState(0)

  const { handleGetOrder } = useGetOrder()

  const handleGetOrderRef = useRef(handleGetOrder)

  useEffect(() => {
    handleGetOrderRef.current = handleGetOrder
  }, [handleGetOrder])

  useEffect(() => {
    let isMounted = true

    const runCalculate = async () => {
      const cacheKey = buildCashFlowMetricsCacheKey("payroll-summary", dateStart, dateFinal)

      const cachedCalculate = getCashFlowMetricsCache<DailyCalculate>(cacheKey)

      if (cachedCalculate) {
        setCalculate(cachedCalculate)
        return
      }

      setIsMetricsLoading(true)

      try {
        await loadCashFlowOrders(handleGetOrderRef.current, dateStart, dateFinal)

        if (!isMounted) {
          return
        }

        const nextCalculate = new DailyCalculate()
        nextCalculate.calculate()
        setCalculate(nextCalculate)
        setCashFlowMetricsCache(cacheKey, nextCalculate)
      } finally {
        if (isMounted) {
          setIsMetricsLoading(false)
        }
      }
    }

    void runCalculate()

    return () => {
      isMounted = false
    }
  }, [dateFinal, dateStart, location.pathname, refreshNonce])

  const payrollRows = useMemo(() => {
    const employeePerformanceMap = new Map(calculate.employeePerformance.map((item) => [item.employeeId, item]))

    return employeeMemory.listData
      .filter((employee) => employee.status === EEnableStatusType.Active && !employee.isDeliveryEmployee)
      .map((employee) => {
        const employeePerformance = employeePerformanceMap.get(employee.id)

        const commissionPercent = employee.isMaster ? 0 : employee.percentCommission.toString().toNumber()

        const fixedSalary = employee.isMaster ? 0 : employee.salaryFixed.toString().toNumber()

        const netRevenue = employee.isMaster ? 0 : (employeePerformance?.netRevenue ?? 0)

        const closedOrders = employee.isMaster ? 0 : (employeePerformance?.closedOrdersCount ?? 0)

        const commissionAmount = employee.isMaster ? 0 : netRevenue * (commissionPercent / 100)

        const payrollTotal = fixedSalary + commissionAmount

        return {
          id: employee.id,
          name: employee.personEntity?.name ?? employee.userName ?? t("cashflow.employee_fallback"),
          role: employee.companyFunction || t("cashflow.no_role"),
          isMaster: employee.isMaster,
          commissionPercent,
          fixedSalary,
          netRevenue,
          closedOrders,
          commissionAmount,
          payrollTotal,
        } as PayrollRow
      })
      .sort((a, b) => {
        if (a.isMaster !== b.isMaster) {
          return a.isMaster ? -1 : 1
        }

        return a.name.localeCompare(b.name, "pt-BR")
      })
  }, [calculate.employeePerformance, t])

  const commissionPayrollRows = useMemo(() => payrollRows.filter((row) => !row.isMaster), [payrollRows])

  const payrollSummary = useMemo(() => {
    return commissionPayrollRows.reduce(
      (acc, row) => {
        acc.totalFixed += row.fixedSalary
        acc.totalCommission += row.commissionAmount
        acc.totalPayroll += row.payrollTotal
        acc.totalRevenue += row.netRevenue
        acc.totalClosedOrders += row.closedOrders
        return acc
      },
      {
        totalFixed: 0,
        totalCommission: 0,
        totalPayroll: 0,
        totalRevenue: 0,
        totalClosedOrders: 0,
      },
    )
  }, [commissionPayrollRows])

  const averageCommissionPercent =
    commissionPayrollRows.length > 0
      ? commissionPayrollRows.reduce((acc, row) => acc + row.commissionPercent, 0) / commissionPayrollRows.length
      : 0

  const handleApplyPeriodFilter = (filters: { dateStart: string; dateEnd: string }, forceRefresh?: boolean) => {
    if (forceRefresh) {
      deleteCashFlowMetricsCache(buildCashFlowMetricsCacheKey("payroll-summary", filters.dateStart, filters.dateEnd))

      if (filters.dateStart === dateStart && filters.dateEnd === dateFinal) {
        setRefreshNonce((currentValue) => currentValue + 1)
      }
    }

    setDateStart(filters.dateStart)
    setDateFinal(filters.dateEnd)
    setShowPeriodFilter(false)
  }

  const handlePrintReport = (format: CashFlowPrintFormat) => {
    const summaryCards = [
      { label: t("cashflow.active_employees"), value: payrollRows.length.toString() },
      { label: t("cashflow.total_payroll"), value: convertToLocalMoney(payrollSummary.totalPayroll) },
      { label: t("cashflow.total_fixed_salary"), value: convertToLocalMoney(payrollSummary.totalFixed) },
      { label: t("cashflow.total_commission"), value: convertToLocalMoney(payrollSummary.totalCommission) },
      { label: t("cashflow.closed_orders_for_commission"), value: payrollSummary.totalClosedOrders.toString() },
      { label: t("cashflow.average_commission_percent"), value: `${averageCommissionPercent.toFixed(2)}%` },
    ]

    const payrollTableHtml = buildTableSectionHtml<PayrollRow>(
      t("cashflow.payroll_table_title"),
      [
        { header: t("cashflow.employee_column"), render: (row) => escapeHtml(row.name) },
        {
          header: t("cashflow.role_column"),
          render: (row) => escapeHtml(row.isMaster ? t("cashflow.prolabore_admin_role") : row.role),
        },
        {
          header: t("cashflow.fixed_salary_column"),
          render: (row) => escapeHtml(row.isMaster ? "-" : convertToLocalMoney(row.fixedSalary)),
        },
        {
          header: t("cashflow.commission_percent_column"),
          render: (row) => (row.isMaster ? "-" : `${row.commissionPercent.toFixed(2)}%`),
        },
        {
          header: t("cashflow.net_revenue_column"),
          render: (row) => escapeHtml(row.isMaster ? "-" : convertToLocalMoney(row.netRevenue)),
        },
        {
          header: t("cashflow.commission_value_column"),
          render: (row) => escapeHtml(row.isMaster ? "-" : convertToLocalMoney(row.commissionAmount)),
        },
        {
          header: t("cashflow.payable_total_column"),
          render: (row) => escapeHtml(row.isMaster ? "-" : convertToLocalMoney(row.payrollTotal)),
        },
      ],
      payrollRows,
      t("cashflow.no_payroll_data"),
    )

    const payrollCompactRows = payrollRows
      .filter((row) => !row.isMaster)
      .slice(0, 20)
      .map((row) => ({
        label: row.name,
        value: convertToLocalMoney(row.payrollTotal),
        subtitle: `${t("cashflow.fixed_salary_column")}: ${convertToLocalMoney(row.fixedSalary)} • ${t("cashflow.commission_value_column")}: ${convertToLocalMoney(row.commissionAmount)} • ${row.commissionPercent.toFixed(2)}%`,
      }))

    const bodyHtml =
      format === "thermal"
        ? [
            buildCompactLabelValueListHtml(summaryCards),
            buildCompactSectionHtml(t("cashflow.payroll_table_title"), payrollCompactRows),
          ].join("")
        : [buildSummaryCardsHtml(summaryCards), payrollTableHtml].join("")

    openPrintReport({
      title: t("cashflow.payroll_page_title"),
      subtitle: [
        companyMemory.companyName || t("cashflow.print_report.restaurant_fallback"),
        t("cashflow.print_report.period_label", {
          startDate: fromInputDate(dateStart).toLocaleDateString("pt-BR"),
          endDate: fromInputDate(dateFinal).toLocaleDateString("pt-BR"),
        }),
        t("cashflow.print_report.generated_at_label", {
          dateTime: getRestaurantNow(companyMemory.timeZoneId).toLocaleDateTimeOrDash(
            "pt-BR",
            companyMemory.timeZoneId,
          ),
        }),
      ].join(" • "),
      bodyHtml,
      format,
    })
  }

  const handleSelectPrintFormat = (format: CashFlowPrintFormat) => {
    setShowPrintFormatPopup(false)
    handlePrintReport(format)
  }

  const payrollComposition = [
    { label: t("cashflow.total_fixed_salary"), value: payrollSummary.totalFixed, color: "#3b82f6" },
    { label: t("cashflow.total_commission"), value: payrollSummary.totalCommission, color: "#8b5cf6" },
  ]

  const payrollRanking = limitFinancialRanking(
    commissionPayrollRows.map((row) => ({ label: row.name, value: row.payrollTotal })),
  )

  const payrollColumns = useMemo<FinancialTableColumn<PayrollRow>[]>(
    () => [
      {
        id: "employee",
        label: t("cashflow.employee_column"),
        sortValue: (row) => row.name,
        mobilePriority: "primary",
        render: (row) => (
          <span className="font-extrabold text-slate-900">
            {row.name}
            {row.isMaster && (
              <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] text-amber-700">
                {t("cashflow.owner_admin_label")}
              </span>
            )}
          </span>
        ),
      },
      {
        id: "role",
        label: t("cashflow.role_column"),
        sortValue: (row) => row.role,
        render: (row) => (row.isMaster ? t("cashflow.prolabore_admin_role") : row.role),
      },
      {
        id: "fixedSalary",
        label: t("cashflow.fixed_salary_column"),
        align: "right",
        sortValue: (row) => row.fixedSalary,
        render: (row) => (row.isMaster ? "-" : convertToLocalMoney(row.fixedSalary)),
      },
      {
        id: "commissionPercent",
        label: t("cashflow.commission_percent_column"),
        align: "right",
        sortValue: (row) => row.commissionPercent,
        render: (row) => (row.isMaster ? "-" : `${row.commissionPercent.toFixed(2)}%`),
      },
      {
        id: "netRevenue",
        label: t("cashflow.net_revenue_column"),
        align: "right",
        sortValue: (row) => row.netRevenue,
        render: (row) => (row.isMaster ? "-" : convertToLocalMoney(row.netRevenue)),
      },
      {
        id: "commissionAmount",
        label: t("cashflow.commission_value_column"),
        align: "right",
        sortValue: (row) => row.commissionAmount,
        render: (row) => (row.isMaster ? "-" : convertToLocalMoney(row.commissionAmount)),
      },
      {
        id: "payrollTotal",
        label: t("cashflow.payable_total_column"),
        align: "right",
        sortValue: (row) => row.payrollTotal,
        mobilePriority: "summary",
        render: (row) => (
          <span className="font-extrabold text-emerald-700">
            {row.isMaster ? "-" : convertToLocalMoney(row.payrollTotal)}
          </span>
        ),
      },
    ],
    [t],
  )

  const paginationLabels = {
    ariaLabel: t("cashflow.table_pagination_aria"),
    previous: t("cashflow.pagination_previous"),
    next: t("cashflow.pagination_next"),
    page: (current: number, total: number) => t("cashflow.pagination_page", { current, total }),
    range: (first: number, last: number, total: number) => t("cashflow.pagination_range", { first, last, total }),
    itemsPerPage: t("cashflow.pagination_size"),
  }

  return (
    <FinancialPageLayout>
      {showPeriodFilter && (
        <CashFlowPeriodFilterPopup
          initialFilters={{ dateStart, dateEnd: dateFinal }}
          today={today}
          onApplyFilters={handleApplyPeriodFilter}
          onClose={() => setShowPeriodFilter(false)}
        />
      )}

      {showPrintFormatPopup && (
        <CashFlowPrintFormatPopup
          onSelectFormat={handleSelectPrintFormat}
          onClose={() => setShowPrintFormatPopup(false)}
        />
      )}

      <FinancialPageHeader
        title={t("cashflow.payroll_page_title")}
        subtitle={t("cashflow.payroll_page_subtitle")}
        period={t("cashflow.print_report.period_label", {
          startDate: fromInputDate(dateStart).toLocaleDateString("pt-BR"),
          endDate: fromInputDate(dateFinal).toLocaleDateString("pt-BR"),
        })}
        filterLabel={t("cashflow.filter_action")}
        printLabel={t("cashflow.print_action")}
        isLoading={isMetricsLoading}
        onFilter={() => setShowPeriodFilter(true)}
        onPrint={() => setShowPrintFormatPopup(true)}
      />

      <div>
        <CashFlowMetricsLoading isVisible={isMetricsLoading} />

        {!isMetricsLoading && (
          <>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
              <MetricCard
                title={t("cashflow.active_employees")}
                value={payrollRows.length.toString()}
                icon={faUsers}
                tone="info"
              />

              <MetricCard
                title={t("cashflow.total_payroll")}
                value={convertToLocalMoney(payrollSummary.totalPayroll)}
                icon={faSackDollar}
                tone="brand"
              />

              <MetricCard
                title={t("cashflow.total_fixed_salary")}
                value={convertToLocalMoney(payrollSummary.totalFixed)}
                icon={faFileInvoiceDollar}
                tone="info"
                helpDescription={t("cashflow.help.payroll_total_fixed_salary.description")}
                helpBullets={getHelpBullets("cashflow.help.payroll_total_fixed_salary.bullets")}
              />

              <MetricCard
                title={t("cashflow.total_commission")}
                value={convertToLocalMoney(payrollSummary.totalCommission)}
                icon={faMoneyBillTransfer}
                tone="violet"
                helpDescription={t("cashflow.help.payroll_total_commission.description")}
                helpBullets={getHelpBullets("cashflow.help.payroll_total_commission.bullets")}
              />

              <MetricCard
                title={t("cashflow.closed_orders_for_commission")}
                value={payrollSummary.totalClosedOrders.toString()}
                icon={faReceipt}
                helpDescription={t("cashflow.help.payroll_closed_orders.description")}
                helpBullets={getHelpBullets("cashflow.help.payroll_closed_orders.bullets")}
              />

              <MetricCard
                title={t("cashflow.average_commission_percent")}
                value={`${averageCommissionPercent.toFixed(2)}%`}
                icon={faPercent}
                helpDescription={t("cashflow.help.payroll_average_commission.description")}
                helpBullets={getHelpBullets("cashflow.help.payroll_average_commission.bullets")}
              />
            </div>

            <GetSpace top={10} />

            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2 ">
              <FinancialChartCard
                title={t("cashflow.payroll_composition_title")}
                subtitle={t("cashflow.payroll_composition_subtitle")}
              >
                <FinancialDonutChart
                  data={payrollComposition}
                  ariaLabel={t("cashflow.payroll_composition_title")}
                  emptyTitle={t("cashflow.no_payroll_data")}
                  formatValue={convertToLocalMoney}
                />
              </FinancialChartCard>

              <FinancialChartCard
                title={t("cashflow.payroll_ranking_title")}
                subtitle={t("cashflow.payroll_ranking_subtitle")}
              >
                <FinancialHorizontalBarChart
                  data={payrollRanking}
                  ariaLabel={t("cashflow.payroll_ranking_title")}
                  emptyTitle={t("cashflow.no_payroll_data")}
                  formatValue={convertToLocalMoney}
                  valueLabel={t("cashflow.payable_total_column")}
                />
              </FinancialChartCard>
            </div>

            <GetSpace top={10} />

            <FinancialDataTable
              title={t("cashflow.payroll_table_title")}
              subtitle={t("cashflow.payroll_table_subtitle")}
              rows={payrollRows}
              getRowKey={(row) => row.id}
              columns={payrollColumns}
              searchText={(row) => `${row.name} ${row.role}`}
              searchLabel={t("cashflow.payroll_search")}
              emptyTitle={t("cashflow.no_payroll_data")}
              initialSort={{ columnId: "employee", direction: "ascending" }}
              paginationLabels={paginationLabels}
            />
          </>
        )}
      </div>
    </FinancialPageLayout>
  )
}
